import React, { useRef, useState } from 'react'
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay'
import { faPause } from '@fortawesome/pro-solid-svg-icons/faPause'
import { faVolume } from '@fortawesome/pro-solid-svg-icons/faVolume'
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'
import { faVolumeSlash } from '@fortawesome/pro-solid-svg-icons/faVolumeSlash'
import clsx from 'clsx'
import i18n from 'src/i18n'
import { ScreenSizes } from 'src/styles'
import { useMediaQuery } from '@hotmart/space-lib/dist/hooks'
import { useUser } from 'src/hook/useUser'
import { ThemeMode } from '@hotmart/space-lib/dist/styles/themes/enum'
import { useTrackDatahubEvents } from 'src/hook/useTrackDatahubEvents'
import { Action, Entity } from 'src/utils/datahubDefaultEvent'
import { AudioPlayerProps } from './audioPlayer.types'
import {
  CustomAudioWrapper,
  CustomIcon,
  CustomInputRange,
  DeleteButton,
  Dropdown,
  PlayPauseButton,
  SpeedButton,
  VolumeButton,
  WrapperCustomInputRange
} from './audioPlayer.styles'
import { PLAYBACK_SPEED_RATES } from './audioPlayer.constants'
import { ElapsedTime } from './components/elapsedTime'

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, onClickDeleteButton, componentName }) => {
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.sm})`)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const { theme } = useUser()
  const { trackClickEvent } = useTrackDatahubEvents()
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [playbackRate, setPlaybackRate] = useState<number>(1)
  const [showSpeedMenu, setShowSpeedMenu] = useState<boolean>(false)
  const [showVolumeMenu, setShowVolumeMenu] = useState<boolean>(false)
  const [volume, setVolume] = useState<number>(1)
  const [isMuted, setIsMuted] = useState<boolean>(false)
  const [shouldRenderVolumeThumb, setShouldRenderVolumeThumb] = useState<boolean>(false)

  const sendTrackingEvent = (action: Action, extraPayload?: Record<string, any>) => {
    if (!componentName) return

    trackClickEvent({
      action,
      payload: {
        entity: Entity.COMMUNITY,
        componentName,
        hasAccessToTheCommunity: true,
        ...extraPayload
      }
    })
  }

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      sendTrackingEvent(Action.CLICK_TO_PLAYING_THE_AUDIO)
      audio.play()
    }
    setIsPlaying(!isPlaying)
  }

  const selectSpeed = (speed: number) => {
    const audio = audioRef.current
    if (!audio) return

    sendTrackingEvent(Action.CLICK_TO_SELECT_AUDIO_SPEED, { audioSpeed: `${speed}x` })

    audio.playbackRate = speed
    requestAnimationFrame(() => setPlaybackRate(speed))
    setShowSpeedMenu(false)
  }

  const toggleMute = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isMuted && volume === 0) {
      const restoredVolume = 1
      audio.volume = restoredVolume
      setVolume(restoredVolume)
    }

    audio.muted = !isMuted
    setIsMuted(!isMuted)

    sendTrackingEvent(Action.CLICK_TO_ADJUST_THE_VOLUME)
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return

    const newVolume = parseFloat(e.target.value)
    audio.volume = newVolume
    requestAnimationFrame(() => setVolume(newVolume))

    setIsMuted(newVolume === 0)
  }

  const handleVolumeChangeEnd = () => {
    sendTrackingEvent(Action.CLICK_TO_ADJUST_THE_VOLUME)
  }

  return (
    <CustomAudioWrapper
      data-test="custom-audio-player"
      className={clsx('_d-flex _align-items-center _border', {
        '_bg-black': theme === ThemeMode.DARK,
        '_bg-white': theme === ThemeMode.LIGHT,
        '_px-4 _py-2': !isMobile,
        '_px-3 _py-1': isMobile
      })}
    >
      <PlayPauseButton data-test="play-pause-button" className="_d-flex" onClick={togglePlay}>
        {isPlaying ? <CustomIcon icon={faPause} /> : <CustomIcon icon={faPlay} />}
      </PlayPauseButton>

      <ElapsedTime audioRef={audioRef} />

      <WrapperCustomInputRange
        className={clsx('_ml-2 _p-2 _d-flex _align-items-center', {
          '_bg-gray-100': showVolumeMenu
        })}
        onMouseEnter={() => setShowVolumeMenu(true)}
        onMouseLeave={() => setShowVolumeMenu(false)}
      >
        <CustomInputRange
          unfilledTrackColor="--gray-200"
          $isDarkMode={theme === ThemeMode.DARK}
          data-test="volume-slider"
          isMobile={isMobile}
          expanded={showVolumeMenu}
          onMouseEnter={() => setShouldRenderVolumeThumb(true)}
          onMouseLeave={() => setShouldRenderVolumeThumb(false)}
          shouldRenderThumb={shouldRenderVolumeThumb}
          className={clsx('_h-1', {
            '_mr-2': showVolumeMenu
          })}
          type="range"
          min={0}
          max={1}
          step={0.01}
          valueProgress={isMuted ? 0 : volume}
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          onMouseUp={handleVolumeChangeEnd}
          onTouchEnd={handleVolumeChangeEnd}
        />

        <VolumeButton data-test="volume-button" onClick={toggleMute} className="_d-flex">
          {isMuted || volume === 0 ? (
            <CustomIcon icon={faVolumeSlash} />
          ) : (
            <CustomIcon icon={faVolume} />
          )}
        </VolumeButton>
      </WrapperCustomInputRange>

      {onClickDeleteButton ? (
        <DeleteButton
          data-test="delete-button"
          onClick={onClickDeleteButton}
          className={clsx('_d-flex _cursor-pointer', {
            '_ml-2': !isMobile,
            '_ml-1': isMobile
          })}
        >
          <CustomIcon icon={faTrash} />
        </DeleteButton>
      ) : (
        <div
          className={clsx('_position-relative _d-flex ', {
            '_ml-4': !isMobile,
            '_ml-2': isMobile
          })}
        >
          <SpeedButton
            onClick={() => setShowSpeedMenu(!showSpeedMenu)}
            className={clsx('_rounded _border _font-weight-extrabold _w-6', {
              '_bg-black _border-white': theme === ThemeMode.DARK,
              '_bg-white _border-black': theme === ThemeMode.LIGHT
            })}
          >
            {playbackRate}x
          </SpeedButton>

          {showSpeedMenu && (
            <Dropdown
              className="_position-absolute _mt-5 _rounded-lg shadow-lg"
              $isDarkMode={theme === ThemeMode.DARK}
            >
              <div className="_p-4 _text-gray-500 _font-14">
                {i18n.t('audio_player.select_speed.title')}
              </div>
              <ul>
                {PLAYBACK_SPEED_RATES.map((speed) => (
                  <li
                    key={speed}
                    onClick={() => selectSpeed(speed)}
                    className={`_px-4 _py-2 _cursor-pointer ${
                      playbackRate === speed ? '_bg-gray-200' : ''
                    }`}
                  >
                    {speed}x
                  </li>
                ))}
              </ul>
            </Dropdown>
          )}
        </div>
      )}

      <audio ref={audioRef} src={src} preload="metadata" onEnded={() => setIsPlaying(false)} />
    </CustomAudioWrapper>
  )
}

export default AudioPlayer
